import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useSearchParams } from "react-router-dom";
import productService from "./services/productService";
import Footer from "./components/layout/Footer";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import CartPage from "./pages/CartPage";
import ProductQuickView from "./components/products/ProductQuickView";
import "./styles/App.css";
import HeaderUp from "./components/layout/HeaderUp";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/Forgotpassword";
import OTPVerification from "./pages/Otpverification";
import MyAccount from "./pages/myaccount/MyAccount";
import WhatsAppButton from "./components/common/WhtsappButton";
import ScrollToTop from "./components/common/ScrollToTop";
import ProtectedRoute from "./context/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import CheckoutPage from "./pages/CheckoutPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import SizeGuide from "./pages/SizeGuide";
import AboutUs from "./pages/AboutUs";
import TermsAndConditions from "./pages/TermsAndConditions";
import ReturnPolicy from "./pages/ReturnPolicy";
import ShippingPolicy from "./pages/ShippingPolicy";
import ContactUs from "./pages/ContactUs";

// Inner component so useSearchParams works inside <Router>
function AppContent() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [searchParams] = useSearchParams();

  // Open QuickView when ?product=ID is in the URL (shared link)
  useEffect(() => {
    const productId = searchParams.get("product");
    if (productId) {
      productService.getProductById(productId)
        .then((res) => {
          if (res?.data) setSelectedProduct(res.data);
        })
        .catch(() => {});
    }
  }, [searchParams]);

  const handleAddToCart = (product) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find(
        (item) =>
          item.id === product.id && item.selectedSize === product.selectedSize
      );

      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id && item.selectedSize === product.selectedSize
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [...prevItems, { ...product, quantity: 1 }];
    });
  };

  const handleUpdateQuantity = (id, size, quantity) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id && item.selectedSize === size
          ? { ...item, quantity }
          : item
      )
    );
  };

  const handleRemoveItem = (id, size) => {
    setCartItems((prevItems) =>
      prevItems.filter(
        (item) => !(item.id === id && item.selectedSize === size)
      )
    );
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleCloseQuickView = () => {
    setSelectedProduct(null);
    // Remove ?product= from URL without page reload
    const url = new URL(window.location.href);
    url.searchParams.delete("product");
    window.history.replaceState({}, "", url.toString());
  };

  return (
    <>
      <ScrollToTop />
      <div className="app">
        <HeaderUp cartCount={cartCount} onProductClick={setSelectedProduct} />

        <main className="main-content">
          <Routes>
            <Route
              path="/myaccount"
              element={
                <ProtectedRoute>
                  <MyAccount />
                </ProtectedRoute>
              }
            >
              <Route index element={<MyAccount />} />
            </Route>

            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/otp" element={<OTPVerification />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/refund-policy" element={<ReturnPolicy />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/size-guide" element={<SizeGuide />} />

            <Route
              path="/"
              element={<HomePage onProductClick={setSelectedProduct} />}
            />
            <Route
              path="/girls"
              element={<ProductsPage onProductClick={setSelectedProduct} />}
            />
            <Route
              path="/boys"
              element={<ProductsPage onProductClick={setSelectedProduct} />}
            />
            <Route
              path="/products"
              element={<ProductsPage onProductClick={setSelectedProduct} />}
            />
            <Route
              path="/products/:name"
              element={<ProductsPage onProductClick={setSelectedProduct} />}
            />
            <Route
              path="/cart"
              element={
                <CartPage
                  cartItems={cartItems}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemove={handleRemoveItem}
                />
              }
            />
            <Route path="/about" element={<AboutUs />} />
          </Routes>
        </main>

        <Footer />

        {selectedProduct && (
          <ProductQuickView
            product={selectedProduct}
            onClose={handleCloseQuickView}
            onAddToCart={handleAddToCart}
          />
        )}
      </div>
      <WhatsAppButton />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
